import React, { useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useHistory, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Toggle } from 'Components/Blocks';
import { JobGuardModal } from 'Components/JobGuard';
import { toggleMemberProfileOpenness } from 'Features/MemberProfile/memberProfileSlice';
import { checkActiveSession } from 'Features/Auth/authSlice';
import { RootState } from 'Store';

const ProfileDetails = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto;
  gap: 24px;
  margin-bottom: 32px;
  padding-bottom: 32px;
  border-bottom: 1px dashed #D6DCE2;
  @media screen and ${(props) => props.theme.breakpoints.md} {
    grid-template-columns: 1fr 1fr;
  }
  @media screen and ${(props) => props.theme.breakpoints.lg} {
    grid-template-columns: 1fr 1fr 1fr;
  }
`;
const ProfileDescription = styled.div`
  display: flex;
  gap: 8px;
  flex-direction: column;
  justify-content: flex-start;
  > span {
    color: #252525;
    font-family: acumin-pro;
    font-size: 16px;
    font-weight: 500;
    &.small {
      font-weight: 300;
    }
  }
`;

const Button = styled.button`
  display: flex;
  justify-self: baseline;
  align-self: baseline;
  align-items: center;
  border-radius: 6px;
  background-color: #D73714;
  border: none;
  color: #ffffff;
  font-family: acumin-pro;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 19px;
  text-align: center;
  padding: 12px 32px;
  cursor: pointer;
  svg {
    margin-right: 10px;
    font-size: 13px;
  }
`;

const InfoBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  strong {
    color: #252525;
    font-family: acumin-pro;
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 0;
    line-height: 19px;
  }
  button {
    color: #0a4ea2;
    font-family: acumin-pro;
    font-size: 14px;
    letter-spacing: 0;
    line-height: 17px;
    text-decoration: underline;
    margin-top: 8px;
    cursor: pointer;
    background: none;
    border: none;
    outline: none;
    padding: 0;
    text-align: left;
  }
  .toggle {
    margin-top: 5px;
  }
`;

interface AdditionalProfileInfo {
  phone: string;
  email: string;
}

const ProfileDetailsComponent = ({
  applicantName,
  applicantId,
  anonymous,
  additionalInfo,
}: {
  applicantName: any;
  applicantId: any;
  anonymous?: boolean;
  additionalInfo?: AdditionalProfileInfo;
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.auth);
  const { results } = useSelector((state: RootState) => state.jobGuard);
  const [jobGuard] = results;
  const [showJobGuardModal, setShowJobGuardModal] = useState(false);
  const isProfileOpen = user?.member_profile?.is_open_profile;

  const toggleProfileVisibility = async () => {
    await dispatch(toggleMemberProfileOpenness());
    await dispatch(checkActiveSession());
  };

  return (
    <ProfileDetails>
      <ProfileDescription>
        <span>{`${applicantName} ${anonymous ? `#${applicantId}` : ''}`}</span>
        <span className="small">{additionalInfo?.email}</span>
        <span className="small">{additionalInfo?.phone}</span>
      </ProfileDescription>
      <ProfileDescription>
        <InfoBlock>
          <strong>{t('profile-visibility')}</strong>
          <Toggle
            isOn={isProfileOpen ?? false}
            onClick={toggleProfileVisibility}
          />
        </InfoBlock>
      </ProfileDescription>
      <ProfileDescription>
        <InfoBlock>
          <strong>{t('job-guard')}</strong>
          <Toggle
            isOn={jobGuard && jobGuard.searches.some((s: any) => s.active)}
            onClick={() => setShowJobGuardModal(true)}
          />
        </InfoBlock>
      </ProfileDescription>
      <Button
        onClick={() => history.push('/create-member-profile?callback=/profile')}
      >
        <FontAwesomeIcon icon={['fas', 'pen']} /> {t('modify-profile')}
      </Button>
      <JobGuardModal
        show={showJobGuardModal}
        closeModal={() => setShowJobGuardModal(false)}
      />
    </ProfileDetails>
  );
};

ProfileDetailsComponent.defaultProps = {
  anonymous: true,
  additionalInfo: null,
};

export default ProfileDetailsComponent;
